<template>
    <div id="content" class="app-content">
        <!-- BEGIN container -->
        <b-container>
            <!-- BEGIN row -->
            <b-row class="justify-content-center">
                <!-- BEGIN col-10 -->
                <b-col xl="12">
                    <!-- BEGIN row -->
                    <b-row>
                        <!-- BEGIN col-9 -->
                        <b-col xl="12">
                            <b-breadcrumb>
                                <b-breadcrumb-item :to="{ name: 'products' }"> PRODUCTS </b-breadcrumb-item>
                                <b-breadcrumb-item v-if="isEditPage" active>
                                    {{ name.toUpperCase() }}
                                </b-breadcrumb-item>
                                <b-breadcrumb-item active>
                                    {{ breadcrumbTitle }}
                                </b-breadcrumb-item>
                            </b-breadcrumb>

                            <b-tabs content-class="mt-3">
                                <b-tab title="Product" :active="$route.name === 'products.edit'" @click="tabGoTo(links.edit)">
                                    <!-- page header -->
                                    <h1 class="page-header">{{ pageTitle }}</h1>
                                    <hr />

                                    <validation-observer ref="productsForm">
                                        <form @submit.prevent="submitFormData">
                                            <b-row>
                                                <b-col cols="8">
                                                    <h4><i class="far fa-heading fa-fw" /> Title</h4>
                                                    <b-card>
                                                        <validation-provider vid="name" name="Name" v-slot="context" rules="required">
                                                            <b-form-group label="Name">
                                                                <b-form-input
                                                                    id="formNameField"
                                                                    placeholder="e.g Mountain Hiking"
                                                                    v-model="name"
                                                                    :state="getValidationState(context)"
                                                                    aria-describedby="formNameFieldError"
                                                                    v-bind="context.ariaInput"
                                                                    :disabled="$cannot('product.create')"
                                                                />

                                                                <b-form-invalid-feedback id="formNameFieldError" v-bind="context.ariaMsg">
                                                                    {{ context.errors[0] }}
                                                                </b-form-invalid-feedback>
                                                            </b-form-group>
                                                        </validation-provider>

                                                        <b-form-group v-if="isEditPage" label="URL">
                                                            <b-form-input
                                                                :value="getProductUrl()"
                                                                readonly
                                                            />
                                                        </b-form-group>
                                                    </b-card>

                                                    <hr />

                                                    <h4><i class="far fa-file-alt fa-fw" /> Description</h4>
                                                    <b-card>
                                                      <validation-provider vid="punchline" name="Punchline" v-slot="context" rules="required">
                                                        <b-form-group label="Punchline (The Amazon Way)" label-for="formPunchlineField">
                                                          <b-form-input
                                                            id="formPunchlineField"
                                                            placeholder="e.g Hiking Like You've Never Seen Before"
                                                            v-model="punchline"
                                                            :state="getValidationState(context)"
                                                            aria-describedby="formPunchlineFieldError"
                                                            v-bind="context.ariaInput"
                                                            :disabled="$cannot('product.create')"
                                                          />

                                                          <b-form-invalid-feedback id="formPunchlineFieldError" v-bind="context.ariaMsg">{{
                                                              context.errors[0]
                                                            }}</b-form-invalid-feedback>
                                                        </b-form-group>
                                                      </validation-provider>

                                                      <validation-provider vid="lead" name="Lead Information" v-slot="context" rules="required">
                                                            <b-form-group label="Lead" label-for="formLeadField">
                                                                <b-textarea
                                                                    id="formLeadField"
                                                                    v-model="lead"
                                                                    placeholder="e.g A wonderful location"
                                                                    rows="3"
                                                                    max-rows="6"
                                                                    :state="getValidationState(context)"
                                                                    :disabled="$cannot('product.create')"
                                                                    aria-describedby="formNameFieldError"
                                                                    v-bind="context.ariaInput"
                                                                />

                                                                <b-form-invalid-feedback id="formNameFieldError" v-bind="context.ariaMsg">{{
                                                                        context.errors[0]
                                                                    }}</b-form-invalid-feedback>
                                                            </b-form-group>
                                                        </validation-provider>

                                                        <validation-provider vid="content" name="Product Content" v-slot="context" rules="required">
                                                            <b-form-group label="Content" label-for="formContentField">
                                                                <wysiwyg-editor v-model="content" id="formContentField" />
                                                                <small
                                                                    id="formCategoryFieldError"
                                                                    v-if="context.errors.length"
                                                                    v-bind="context.ariaMsg"
                                                                    class="text-danger form-text"
                                                                >{{ context.errors[0] }}</small
                                                                >
                                                            </b-form-group>
                                                        </validation-provider>

                                                        <validation-provider vid="experience_description" name="Experience Description" v-slot="context" rules="required">
                                                          <b-form-group label="Experience Description" label-for="formExperienceDescriptionField">
                                                            <b-textarea
                                                              id="formExperienceDescriptionField"
                                                              v-model="experience_description"
                                                              placeholder="e.g See different places and galleries"
                                                              rows="3"
                                                              max-rows="6"
                                                              :state="getValidationState(context)"
                                                              :disabled="$cannot('product.create')"
                                                              aria-describedby="formExperienceDescriptionFieldError"
                                                              v-bind="context.ariaInput"
                                                            />

                                                            <b-form-invalid-feedback id="formExperienceDescriptionFieldError" v-bind="context.ariaMsg">{{
                                                                context.errors[0]
                                                              }}</b-form-invalid-feedback>
                                                          </b-form-group>
                                                        </validation-provider>

                                                        <hr />

                                                        <div class="mb-2">Bullet Points</div>

                                                        <template v-if="bullet_points">
                                                            <b-row v-for="(point, index) in bullet_points" :key="index" class="mb-3">
                                                                <b-col cols="12">
                                                                    <keep-alive>
                                                                        <validation-provider name="Bullet points" v-slot="context" :vid="`bullet_points.${index}.content`">
                                                                            <b-input-group style="position: relative;">
                                                                                <b-form-input
                                                                                    placeholder=""
                                                                                    v-model="bullet_points[index].emoji"
                                                                                    :disabled="$cannot('product.create')"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0; max-width: 100px;"
                                                                                />

                                                                                <b-form-input
                                                                                    placeholder="e.g Wonderful tracks"
                                                                                    v-model="bullet_points[index].content"
                                                                                    :state="getValidationState(context)"
                                                                                    :aria-describedby="`formBulletPonts${index}FieldError`"
                                                                                    :disabled="$cannot('product.create')"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                    v-bind="context.ariaInput"
                                                                                />

                                                                                <template #append>
                                                                                    <b-button
                                                                                        variant="danger"
                                                                                        @click="() => removeBulletPoint(index)"
                                                                                        :disabled="$cannot('product.create')"
                                                                                    >
                                                                                        <strong>&times;</strong>
                                                                                    </b-button>
                                                                                </template>

                                                                                <emoji-picker @emoji="insertEmoji" :search="emojiSearch" class="emoji-invoker-wrapper">
                                                                                  <button
                                                                                    type="button"
                                                                                    class="emoji-invoker"
                                                                                    slot="emoji-invoker"
                                                                                    slot-scope="{ events: { click: clickEvent } }"
                                                                                    @click.stop="clickEvent"
                                                                                  >
                                                                                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current text-grey">
                                                                                      <path d="M0 0h24v24H0z" fill="none"/>
                                                                                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                                                                                    </svg>
                                                                                  </button>
                                                                                  <div slot="emoji-picker" slot-scope="{ emojis }">
                                                                                    <div class="emoji-picker" :style="{ top: '-5px', left: '30px' }">
                                                                                      <div class="emoji-picker__search">
                                                                                        <input type="text" v-model="emojiSearch" v-focus>
                                                                                      </div>
                                                                                      <div>
                                                                                        <div v-for="(emojiGroup, category) in emojis" :key="category" style="margin-top: 0.75rem;">
                                                                                          <h5>{{ category }}</h5>
                                                                                          <div class="emojis">
                                                                                            <span
                                                                                              v-for="(emoji, emojiName) in emojiGroup"
                                                                                              :key="emojiName"
                                                                                              @click="insertEmoji(emoji, index)"
                                                                                              :title="emojiName"
                                                                                            >{{ emoji }}</span>
                                                                                          </div>
                                                                                        </div>
                                                                                      </div>
                                                                                    </div>
                                                                                  </div>
                                                                                </emoji-picker>
                                                                            </b-input-group>

                                                                            <b-form-invalid-feedback :state="getValidationState(context)" v-bind="context.ariaMsg">
                                                                                {{context.errors[0]?.replace(`bullet_points.${index}.content`, 'content') }}
                                                                            </b-form-invalid-feedback>
                                                                        </validation-provider>
                                                                    </keep-alive>
                                                                </b-col>
                                                            </b-row>
                                                        </template>

                                                        <b-button
                                                            variant="light"
                                                            size="sm"
                                                            @click="addEmptyBulletPoint"
                                                            :disabled="$cannot('product.create')"
                                                        >
                                                            Add Point
                                                        </b-button>
                                                    </b-card>

                                                    <div v-if="type === 'themed' || type === 'experience_cash_voucher'">
                                                        <hr />

                                                        <h4 ><i class="far fa-text fa-fw" /> Cash Voucher Data</h4>
                                                        <b-card>
                                                            <template v-if="type === 'themed'">
                                                                <label>Description</label>
                                                                <b-form-input
                                                                    v-if="type === 'themed'"
                                                                    placeholder="Description"
                                                                    v-model="experience_cash_voucher_description"
                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                />

                                                                <hr>

                                                                <label>Amount Description</label>
                                                                <b-form-input
                                                                    v-if="type === 'themed'"
                                                                    placeholder="Amount Description"
                                                                    v-model="experience_cash_voucher_amount_description"
                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                />
                                                                <hr>

                                                                <label>Themed Content Description</label>
                                                                <b-textarea
                                                                    v-if="type === 'themed'"
                                                                    rows="5"
                                                                    placeholder="Themed Content Description"
                                                                    v-model="themed_content_description"
                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                />

                                                                <hr>
                                                                <label>Themed Error Message</label>
                                                                <b-textarea
                                                                    v-if="type === 'themed'"
                                                                    rows="5"
                                                                    placeholder="Themed Error Message"
                                                                    v-model="experience_cash_voucher_error_message"
                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                />
                                                                <hr>
                                                            </template>

                                                            <div v-if="experience_cash_voucher_data.length && (type === 'themed' || type === 'experience_cash_voucher')">
                                                                <b-row v-for="(cash_voucher_data, index) in experience_cash_voucher_data" :key="index" class="mb-3">
                                                                    <b-col cols="12">
                                                                        <validation-provider name="Experience Cash Voucher Data" v-slot="context" :vid="`experience_cash_voucher_data.${index}.content`">
                                                                            <b-input-group>
                                                                                <b-form-input
                                                                                    placeholder="Description"
                                                                                    v-model="experience_cash_voucher_data[index].name"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                    v-bind="context.ariaInput"
                                                                                />
                                                                                <b-form-input
                                                                                    placeholder="Sum Variation Name"
                                                                                    v-model="experience_cash_voucher_data[index].sum_variation_name"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                    v-bind="context.ariaInput"
                                                                                />
                                                                                <b-form-input
                                                                                    placeholder="Price"
                                                                                    type="number"
                                                                                    v-model="experience_cash_voucher_data[index].price"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                    v-bind="context.ariaInput"
                                                                                />

                                                                                <template #append>
                                                                                    <strong @click="() => removeExperienceCashVoucherData(index)" class="text-red h6 mt-2 ml-1" style="font-size:18px; cursor: pointer;">&times;</strong>
                                                                                </template>
                                                                            </b-input-group>
                                                                        </validation-provider>
                                                                    </b-col>
                                                                </b-row>

                                                                <b-col cols="12">
                                                                    <validation-provider name="Experience Cash Voucher Data" v-slot="context" :vid="`experience_cash_voucher_data.${index}.content`">
                                                                        <b-input-group>
                                                                            <b-form-input
                                                                                placeholder="Min"
                                                                                v-model="experience_cash_voucher_max"
                                                                                style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                v-bind="context.ariaInput"
                                                                            />
                                                                            <b-form-input
                                                                                placeholder="Max"
                                                                                v-model="experience_cash_voucher_min"
                                                                                style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                v-bind="context.ariaInput"
                                                                            />
                                                                        </b-input-group>
                                                                    </validation-provider>
                                                                </b-col>
                                                            </div>
                                                            <br>

                                                            <b-button
                                                                v-if="type === 'themed' || type === 'experience_cash_voucher'"
                                                                variant="light"
                                                                size="sm"
                                                                @click="addExperienceCashVoucherData"
                                                            >
                                                                Add Cash Voucher Data
                                                            </b-button>
                                                        </b-card>
                                                    </div>

                                                    <div v-if="type === 'experience_cash_voucher'">
                                                        <hr>
                                                        <h4><i class="far fa-text fa-fw" /> Experience (CV) Product Data</h4>
                                                        <b-card>
                                                            <template>
                                                                <b-form-group label="Name">
                                                                    <b-form-input
                                                                        placeholder="Name"
                                                                        v-model="experience_cash_voucher_product_variation_data.name"
                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    />
                                                                </b-form-group>

                                                                <b-form-group label="About 1">
                                                                    <b-form-input
                                                                        placeholder="About 1"
                                                                        v-model="experience_cash_voucher_product_variation_data.about_1"
                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    />
                                                                </b-form-group>

                                                                <b-form-group label="Address">
                                                                    <b-form-input
                                                                        placeholder="Address"
                                                                        v-model="experience_cash_voucher_product_variation_data.address"
                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    />
                                                                </b-form-group>

                                                                <b-form-group label="About 2">
                                                                    <b-form-input
                                                                        placeholder="About 2"
                                                                        v-model="experience_cash_voucher_product_variation_data.about_2"
                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    />
                                                                </b-form-group>

                                                                <b-form-group label="What is included">
                                                                    <b-form-input
                                                                        placeholder="What is included"
                                                                        v-model="experience_cash_voucher_product_variation_data.what_is_included"
                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    />
                                                                </b-form-group>
                                                            </template>
                                                        </b-card>
                                                    </div>

                                                    <div v-if="type === 'bundle_product' || type === 'bundle_cash_voucher'">
                                                        <hr />

                                                        <h4><i class="far fa-text fa-fw" /> Bundle Products</h4>
                                                        <b-card>
                                                            <div v-for="(combo, index) in bundleCombinations" :key="index" class="row mb-2">
                                                                <div :class="type === 'bundle_product' ? 'col-md-5' : 'col-md-4'">
                                                                    <multi-select
                                                                        v-model="combo.variation1"
                                                                        :options="allActiveProductVariations"
                                                                        label="name"
                                                                        track-by="id"
                                                                        class="mt-3"
                                                                        value-field="id"
                                                                        text-field="name"
                                                                        :multiple="false"
                                                                        :clear-on-select="true"
                                                                        :preserve-search="true"
                                                                        placeholder="Choose Main Variation"
                                                                    />
                                                                </div>
                                                                <div v-if="type === 'bundle_product'" :class="type === 'bundle_product' ? 'col-md-5' : 'col-md-4'">
                                                                    <multi-select
                                                                        v-model="combo.variation2"
                                                                        :options="filteredVariations(combo.variation1)"
                                                                        label="name"
                                                                        track-by="id"
                                                                        class="mt-3"
                                                                        value-field="id"
                                                                        text-field="name"
                                                                        :multiple="false"
                                                                        :clear-on-select="true"
                                                                        :preserve-search="true"
                                                                        placeholder="Choose variation"
                                                                    />
                                                                </div>
                                                                <div v-if="type === 'bundle_cash_voucher'" class="col-md-4" >
                                                                    <multi-select
                                                                        v-model="combo.variation2"
                                                                        :options="[
                                                  { id: 0, name: 'Open Giftcard' },
                                                  ...filteredCashVoucherProducts(combo.variation1)
                                                ]"
                                                                        label="name"
                                                                        track-by="id"
                                                                        class="mt-3"
                                                                        value-field="id"
                                                                        text-field="name"
                                                                        :multiple="false"
                                                                        :clear-on-select="true"
                                                                        :preserve-search="true"
                                                                        placeholder="Choose cash-voucher product"
                                                                    />
                                                                </div>
                                                                <div v-if="type === 'bundle_cash_voucher'" class="col-md-3">
                                                                    <b-form-input
                                                                        v-model.number="combo.amount"
                                                                        class="mt-3"
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="e.g 18"
                                                                    />
                                                                </div>
                                                                <div class="col-md-1 mt-3">
                                                                    <b-button @click="removeCombination(index)" variant="danger">&times;</b-button>
                                                                </div>
                                                            </div>
                                                            <b-button class="mt-3" @click="addCombination">Add Variation Combination</b-button>
                                                        </b-card>
                                                    </div>

                                                    <div v-if="type === 'tier_product'">
                                                        <hr />

                                                        <h4><i class="far fa-text fa-fw" /> Tier Product Data</h4>
                                                        <b-card>
                                                            <b-form-group label="Tier name" class="mb-3">
                                                              <b-form-input
                                                                v-model="tier_name"
                                                                placeholder="Tier name"
                                                              />
                                                            </b-form-group>

                                                            <b-form-group label="Tier name in BuyBox" class="mb-3">
                                                              <b-form-input
                                                                v-model="tier_name_buybox"
                                                                placeholder="Tier name in BuyBox"
                                                              />
                                                            </b-form-group>

                                                            <b-form-checkbox
                                                              v-model="hide_tier_names"
                                                              class="ml-1 mb-3">
                                                              Hide tier names
                                                            </b-form-checkbox>

                                                          <div class="mb-2">Tier products</div>

                                                            <div
                                                                v-for="(tier, index) in tier_product_data"
                                                                :key="index"
                                                                class="row mb-2"
                                                            >
                                                              <div class="d-flex w-100">
                                                                <div class="col-md-4">
                                                                  <multi-select
                                                                    v-model="tier.variation_id"
                                                                    :options="variationsByPlace()"
                                                                    label="name"
                                                                    track-by="id"
                                                                    :clear-on-select="false"
                                                                    placeholder="Choose Variation"
                                                                  />
                                                                </div>
                                                                <div class="col-md-4">
                                                                  <multi-select
                                                                    v-model="tier.place_id"
                                                                    :options="places"
                                                                    label="name"
                                                                    multiple
                                                                    track-by="id"
                                                                    :clear-on-select="false"
                                                                    placeholder="Choose Place"
                                                                  />
                                                                </div>
                                                                <div class="col-md-3">
                                                                    <b-form-input
                                                                        v-model="tier.description"
                                                                        placeholder="Description"
                                                                        class="mb-1"
                                                                    />
                                                                    <b-form-input
                                                                        v-model="tier.status"
                                                                        placeholder="Status"
                                                                    />
                                                                </div>
                                                                <div class="col-md-1">
                                                                    <b-button @click="removeTierProduct(index)" variant="danger">×</b-button>
                                                                </div>
                                                              </div>

                                                              <div>
                                                                <b-form-group label="Content">
                                                                  <wysiwyg-editor v-model="tier.content" />
                                                                </b-form-group>
                                                              </div>

                                                              <hr class="w-100" />
                                                            </div>
                                                            <b-button
                                                                variant="light"
                                                                size="sm"
                                                                @click="addTierProduct"
                                                            >
                                                                Add Tier Product
                                                            </b-button>
                                                        </b-card>
                                                    </div>

                                                    <div v-if="type === 'hotel'">
                                                        <hr />

                                                        <h4><i class="far fa-text fa-fw" /> Hotel Content</h4>
                                                        <b-card>
                                                            <template>
                                                                <b-col cols="8" class="mb-3">
                                                                    <keep-alive>
                                                                        <validation-provider v-slot="context">
                                                                            <b-input-group>
                                                                                <b-form-input
                                                                                    placeholder="Count"
                                                                                    v-model="hotel_count"
                                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                    v-bind="context.ariaInput"
                                                                                />

                                                                                <b-form-checkbox
                                                                                    v-model="hotel_map_only"
                                                                                    class="mt-1 ml-2"
                                                                                    v-bind="context.ariaInput">
                                                                                    Hotel Map Only
                                                                                </b-form-checkbox>

                                                                                <b-form-checkbox
                                                                                    v-model="is_non_hotel"
                                                                                    class="mt-1 ml-2"
                                                                                    v-bind="context.ariaInput">
                                                                                    Is Non Hotel
                                                                                </b-form-checkbox>
                                                                            </b-input-group>
                                                                        </validation-provider>
                                                                    </keep-alive>
                                                                </b-col>

                                                                <div v-if="hotel_content.length">
                                                                    <b-row v-for="(content, index) in hotel_content" :key="index" class="mb-3">
                                                                        <b-col cols="12">
                                                                            <validation-provider name="Hotel Content" v-slot="context" :vid="`hotel_content.${index}.content`">
                                                                                <b-input-group>
                                                                                    <b-form-input
                                                                                        placeholder="Name"
                                                                                        v-model="hotel_content[index].name"
                                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                        v-bind="context.ariaInput"
                                                                                    />
                                                                                    <b-form-input
                                                                                        placeholder="Prizren, Kosova"
                                                                                        v-model="hotel_content[index].location"
                                                                                        style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                                        v-bind="context.ariaInput"
                                                                                    />
                                                                                    <b-form-input
                                                                                        v-model="hotel_content[index].coordinates"
                                                                                        v-bind="context.ariaInput"
                                                                                        placeholder="42.221041, 20.751225"
                                                                                    />

                                                                                    <div style="width: 150px;">
                                                                                        <b-form-file
                                                                                            @click="$refs.file.click()"
                                                                                            variant="primary"
                                                                                            class="btn"
                                                                                            type="file"
                                                                                            ref="file"
                                                                                            @change="pick($event, index)"
                                                                                            v-model="hotel_content[index].image"
                                                                                            accept="image/*"
                                                                                        />
                                                                                        <small v-if="hotel_content[index].image">
                                                                                            <a :href="hotel_content[index].image" target="_blank">
                                                                                                <i class="fas fa-download"/> download current
                                                                                            </a>
                                                                                        </small>
                                                                                    </div>

                                                                                    <template #append>
                                                                                        <strong @click="() => removeHotelContent(index)" class="text-red h6 mt-2 ml-1" style="font-size:18px; cursor: pointer;">&times;</strong>
                                                                                    </template>
                                                                                </b-input-group>

                                                                                <b-form-invalid-feedback :state="getValidationState(context)" v-bind="context.ariaMsg">
                                                                                    {{context.errors[0]?.replace(`hotel_content.${index}.content`, 'content') }}
                                                                                </b-form-invalid-feedback>
                                                                            </validation-provider>
                                                                        </b-col>
                                                                    </b-row>
                                                                </div>
                                                            </template>

                                                            <b-button
                                                                variant="light"
                                                                size="sm"
                                                                @click="addEmptyHotelContent"
                                                            >
                                                                Add Hotel Content
                                                            </b-button>
                                                        </b-card>
                                                    </div>

                                                    <hr />

                                                    <h4><i class="far fa-photo-video fa-fw" /> Embeds</h4>
                                                    <b-card>
                                                        <b-form-group label="Videos">
                                                          <input
                                                            class="invisible no-dimensions"
                                                            accept="video/mp4,video/x-m4v,video/*"
                                                            ref="desktopVideo"
                                                            type="file"
                                                            @change="uploadDesktopVideo"
                                                          />
                                                          <b-button variant="primary" class="mb-2" @click="pickDesktopVideo" :disabled="uploading.desktop">
                                                            <b-spinner v-show="uploading.desktop" class="mr-2" small label="Loading" />
                                                            {{ video.desktop ? 'Upload different video' : 'Upload video' }}
                                                          </b-button>

                                                          <div class="w-25 h-auto my-2">
                                                            <b-embed
                                                              type="video"
                                                              :src="video.desktop"
                                                              allowfullscreen
                                                              controls
                                                              v-if="video.desktop"
                                                            />
                                                          </div>

                                                          <b-form-checkbox v-model="video.mobile_same_as_desktop" @change="toggleSameVideoForMobile">
                                                            Use the same video for mobile
                                                          </b-form-checkbox>

                                                          <div class="mt-2" v-if="!video.mobile_same_as_desktop">
                                                            <input
                                                              class="invisible no-dimensions"
                                                              accept="video/mp4,video/x-m4v,video/*"
                                                              ref="mobileVideo"
                                                              type="file"
                                                              @change="uploadMobileVideo"
                                                            />
                                                            <b-button variant="primary" class="mb-2" @click="pickMobileVideo" :disabled="uploading.mobile">
                                                              <b-spinner v-show="uploading.mobile" class="mr-2" small label="Loading" />
                                                              {{ video.mobile ? 'Upload different mobile video' : 'Upload mobile video' }}
                                                            </b-button>

                                                            <div class="w-25 h-auto my-2">
                                                              <b-embed
                                                                type="video"
                                                                :src="video.mobile"
                                                                allowfullscreen
                                                                controls
                                                                v-if="video.mobile"
                                                              />
                                                            </div>
                                                          </div>
                                                        </b-form-group>

                                                        <b-form-group label="Video Content" label-for="formVideoContentField">
                                                            <b-form-text id="formVideoContentFieldDesc">
                                                                Paste in an embed from YouTube of similar sites.
                                                            </b-form-text>
                                                            <b-textarea
                                                                id="formVideoContentField"
                                                                v-model="video_content"
                                                                placeholder
                                                                rows="3"
                                                                max-rows="6"
                                                                aria-describedby="formVideoContentFieldDesc"
                                                                :disabled="$cannot('product.create')"
                                                            />
                                                        </b-form-group>
                                                    </b-card>

                                                    <hr />

                                                    <h4><i class="far fa-details" /> Default About</h4>
                                                    <b-card>
                                                        <b-form-group label="Participants Text">
                                                            <b-form-input v-model="participants_text" placeholder="e.g wear hiking boots" />
                                                        </b-form-group>

                                                        <b-form-group label="When">
                                                            <b-form-input v-model="when" placeholder="e.g At sunrise" />
                                                        </b-form-group>

                                                        <b-form-group label="Duration">
                                                            <b-form-input v-model="duration" placeholder="e.g 10 hours" />
                                                        </b-form-group>

                                                        <b-form-group label="Requirements">
                                                            <b-form-input v-model="requirements" placeholder="e.g come with a physical report" />
                                                            <b-form-text>{{ 225 - requirements.length }} / 225 characaters remaing</b-form-text>
                                                        </b-form-group>

                                                        <b-form-group label="Min Age">
                                                            <b-form-input type="number" v-model="min_age" placeholder="e.g 18" />
                                                        </b-form-group>

                                                      <div class="mb-2">Custom useful info</div>

                                                      <template v-if="custom_useful_info">
                                                        <b-row v-for="(info, index) in custom_useful_info" :key="index" class="mb-3">
                                                          <b-col cols="12">
                                                            <keep-alive>
                                                              <validation-provider name="Custom useful info" v-slot="context" :vid="`custom_useful_info.${index}.info`">
                                                                <b-input-group>
                                                                  <b-form-input
                                                                    placeholder="e.g Does it require wearing green pants?"
                                                                    v-model="custom_useful_info[index].info"
                                                                    :state="getValidationState(context)"
                                                                    :aria-describedby="`formCustomUsefulInfo${index}FieldError`"
                                                                    :disabled="$cannot('product.create')"
                                                                    style="border-top-right-radius: 0; border-bottom-right-radius: 0;"
                                                                    v-bind="context.ariaInput"
                                                                  />

                                                                  <b-form-input
                                                                    placeholder="e.g Definitely!"
                                                                    v-model="custom_useful_info[index].details"
                                                                    :state="getValidationState(context)"
                                                                    :aria-describedby="`formCustomUsefulInfo${index}FieldError`"
                                                                    :disabled="$cannot('product.create')"
                                                                    style="border-radius: 0;"
                                                                    v-bind="context.ariaInput"
                                                                  />

                                                                  <template #append>
                                                                    <b-button
                                                                      variant="danger"
                                                                      @click="() => removeCustomUsefulInfo(index)"
                                                                      :disabled="$cannot('product.create')"
                                                                    >
                                                                      <strong>&times;</strong>
                                                                    </b-button>
                                                                  </template>
                                                                </b-input-group>

                                                                <b-form-invalid-feedback :state="getValidationState(context)" v-bind="context.ariaMsg">
                                                                  {{context.errors[0]?.replace(`custom_useful_info.${index}.info`, 'info') }}
                                                                </b-form-invalid-feedback>
                                                              </validation-provider>
                                                            </keep-alive>
                                                          </b-col>
                                                        </b-row>
                                                      </template>

                                                      <b-button
                                                        variant="light"
                                                        size="sm"
                                                        @click="addEmptyCustomUsefulInfo"
                                                        :disabled="$cannot('product.create')"
                                                      >
                                                        Add custom useful info
                                                      </b-button>
                                                    </b-card>
                                                    <hr />
                                                    <div v-if="type === 'digital'">
                                                      <h4><i class="far fa-heading fa-fw" /> Live Settings</h4>
                                                      <b-card>
                                                        <validation-provider
                                                          vid="how_it_works"
                                                          name="How it works"
                                                          v-slot="context"
                                                          rules="required"
                                                        >
                                                          <b-form-group label="How it works">
                                                            <wysiwyg-editor
                                                              id="formHowItWorksField"
                                                              placeholder="e.g you need a big bowl"
                                                              v-model="how_it_works"
                                                              :state="getValidationState(context)"
                                                              aria-describedby="formHowItWorksFieldError"
                                                              v-bind="context.ariaInput"
                                                              :disabled="$cannot('product.create')"
                                                            />

                                                            <b-form-invalid-feedback id="formHowItWorksFieldError" v-bind="context.ariaMsg">
                                                              {{ context.errors[0] }}
                                                            </b-form-invalid-feedback>
                                                          </b-form-group>
                                                        </validation-provider>

                                                    <!--                              <b-form-group label="Shopping List">-->
                                                    <!--                                <draggable v-model="shopping_list" handle=".handle">-->
                                                    <!--                                  <b-row v-for="(item, index) in shopping_list" :key="index" class="mb-2">-->
                                                    <!--                                    <b-col cols="auto">-->
                                                    <!--                                      <i class="fa fa-align-justify handle" />-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col cols="8">-->
                                                    <!--                                      <validation-provider name="Name" v-slot="context">-->
                                                    <!--                                        <b-form-input-->
                                                    <!--                                          placeholder="Name"-->
                                                    <!--                                          v-model="shopping_list[index].name"-->
                                                    <!--                                          :state="getValidationState(context)"-->
                                                    <!--                                          v-bind="context.ariaInput"-->
                                                    <!--                                          :disabled="$cannot('product.create')"-->
                                                    <!--                                        />-->

                                                    <!--                                        <b-form-invalid-feedback v-bind="context.ariaMsg">-->
                                                    <!--                                          {{ context.errors[0] }}-->
                                                    <!--                                        </b-form-invalid-feedback>-->
                                                    <!--                                      </validation-provider>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col>-->
                                                    <!--                                      <validation-provider name="Price" v-slot="context">-->
                                                    <!--                                        <b-form-input-->
                                                    <!--                                          placeholder="Price"-->
                                                    <!--                                          v-model="shopping_list[index].price"-->
                                                    <!--                                          :state="getValidationState(context)"-->
                                                    <!--                                          v-bind="context.ariaInput"-->
                                                    <!--                                          :disabled="$cannot('product.create')"-->
                                                    <!--                                        />-->

                                                    <!--                                        <b-form-invalid-feedback v-bind="context.ariaMsg">-->
                                                    <!--                                          {{ context.errors[0] }}-->
                                                    <!--                                        </b-form-invalid-feedback>-->
                                                    <!--                                      </validation-provider>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col cols="auto" v-if="shopping_list.length > 1">-->
                                                    <!--                                      <b-button-->
                                                    <!--                                        variant="light"-->
                                                    <!--                                        @click="removeListItem(shopping_list, index, defaultShoppingList)"-->
                                                    <!--                                      >-->
                                                    <!--                                        &times;-->
                                                    <!--                                      </b-button>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                  </b-row>-->
                                                    <!--                                </draggable>-->
                                                    <!--                                <b-row align-h="end">-->
                                                    <!--                                  <b-col cols="auto">-->
                                                    <!--                                    <b-button variant="link" @click="addListItem(shopping_list, defaultShoppingList)">-->
                                                    <!--                                      Add Row-->
                                                    <!--                                    </b-button>-->
                                                    <!--                                  </b-col>-->
                                                    <!--                                </b-row>-->
                                                    <!--                              </b-form-group>-->

                                                    <!--                              <validation-provider-->
                                                    <!--                                vid="what_is_learned_title"-->
                                                    <!--                                name="What they're learning title"-->
                                                    <!--                                v-slot="context"-->
                                                    <!--                              >-->
                                                    <!--                                <b-form-group label="What they're learning title">-->
                                                    <!--                                  <b-form-input-->
                                                    <!--                                    id="formWhatTheyLearningTitle"-->
                                                    <!--                                    placeholder="e.g how to make a casserole"-->
                                                    <!--                                    v-model="what_is_learned_title"-->
                                                    <!--                                    :state="getValidationState(context)"-->
                                                    <!--                                    aria-describedby="formWhatTheyLearningTitleError"-->
                                                    <!--                                    v-bind="context.ariaInput"-->
                                                    <!--                                    :disabled="$cannot('product.create')"-->
                                                    <!--                                  />-->

                                                    <!--                                  <b-form-invalid-feedback id="formWhatTheyLearningTitleError" v-bind="context.ariaMsg">-->
                                                    <!--                                    {{ context.errors[0] }}-->
                                                    <!--                                  </b-form-invalid-feedback>-->
                                                    <!--                                </b-form-group>-->
                                                    <!--                              </validation-provider>-->

                                                    <!--                              <b-form-group label="What they're learning content">-->
                                                    <!--                                <draggable v-model="what_is_learned" handle=".handle">-->
                                                    <!--                                  <b-row v-for="(item, index) in what_is_learned" :key="index" class="mb-2">-->
                                                    <!--                                    <b-col cols="auto">-->
                                                    <!--                                      <i class="fa fa-align-justify handle" />-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col>-->
                                                    <!--                                      <validation-provider name="Title" v-slot="context">-->
                                                    <!--                                        <b-form-input-->
                                                    <!--                                          v-model="what_is_learned[index].title"-->
                                                    <!--                                          placeholder="Title"-->
                                                    <!--                                          :state="getValidationState(context)"-->
                                                    <!--                                          v-bind="context.ariaInput"-->
                                                    <!--                                          :disabled="$cannot('product.create')"-->
                                                    <!--                                        />-->

                                                    <!--                                        <b-form-invalid-feedback v-bind="context.ariaMsg">-->
                                                    <!--                                          {{ context.errors[0] }}-->
                                                    <!--                                        </b-form-invalid-feedback>-->
                                                    <!--                                      </validation-provider>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col cols="auto" v-if="what_is_learned.length > 1">-->
                                                    <!--                                      <b-button-->
                                                    <!--                                        variant="light"-->
                                                    <!--                                        @click="removeListItem(what_is_learned, index, defaultWhatIsLearned)"-->
                                                    <!--                                      >-->
                                                    <!--                                        &times;-->
                                                    <!--                                      </b-button>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                  </b-row>-->
                                                    <!--                                </draggable>-->
                                                    <!--                                <b-row align-h="end">-->
                                                    <!--                                  <b-col cols="auto">-->
                                                    <!--                                    <b-button-->
                                                    <!--                                      variant="link"-->
                                                    <!--                                      @click="addListItem(what_is_learned, defaultWhatIsLearned)"-->
                                                    <!--                                    >-->
                                                    <!--                                      Add Row-->
                                                    <!--                                    </b-button>-->
                                                    <!--                                  </b-col>-->
                                                    <!--                                </b-row>-->
                                                    <!--                              </b-form-group>-->

                                                    <!--                              <b-form-group label="What's included">-->
                                                    <!--                                <draggable v-model="whats_included" handle=".handle">-->
                                                    <!--                                  <b-row v-for="(item, index) in whats_included" :key="index" class="mb-2">-->
                                                    <!--                                    <b-col cols="auto">-->
                                                    <!--                                      <i class="fa fa-align-justify handle" />-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                    <b-col cols="auto">-->
                                                    <!--                                      <c-img-->
                                                    <!--                                        :src="getIcon(item.icon)"-->
                                                    <!--                                        width="35"-->
                                                    <!--                                        style="cursor: pointer"-->
                                                    <!--                                        @click="$bvModal.show(`whatLearnedIcon${index}`)"-->
                                                    <!--                                      />-->

                                                    <!--                                      <b-modal :id="`whatLearnedIcon${index}`" title="Choose icon" ok-only>-->
                                                    <!--                                        <b-row>-->
                                                    <!--                                          <b-col-->
                                                    <!--                                            v-for="(icon, id) in whatsIncludedIcons"-->
                                                    <!--                                            :key="id"-->
                                                    <!--                                            cols="3"-->
                                                    <!--                                            class="mb-2"-->
                                                    <!--                                            style="cursor: pointer"-->
                                                    <!--                                            @click="updateWhatLearnedIcon(index, icon)"-->
                                                    <!--                                          >-->
                                                    <!--                                            <div-->
                                                    <!--                                              class="d-flex flex-column justify-content-center align-items-center h-64 border"-->
                                                    <!--                                              :class="[item.icon === icon ? 'border-primary' : 'border-secondary']"-->
                                                    <!--                                              style="min-height: 74px; border-width: 2px !important"-->
                                                    <!--                                            >-->
                                                    <!--                                              <c-img :src="getIcon(icon)" width="50" class="img-center" />-->
                                                    <!--                                            </div>-->
                                                    <!--                                            <p class="text-center">{{ icon }}</p>-->
                                                    <!--                                          </b-col>-->
                                                    <!--                                        </b-row>-->
                                                    <!--                                      </b-modal>-->
                                                    <!--                                    </b-col>-->

                                                    <!--                                    <b-col>-->
                                                    <!--                                      <validation-provider name="Name" v-slot="context">-->
                                                    <!--                                        <b-form-input-->
                                                    <!--                                          placeholder="Title"-->
                                                    <!--                                          v-model="whats_included[index].title"-->
                                                    <!--                                          :state="getValidationState(context)"-->
                                                    <!--                                          v-bind="context.ariaInput"-->
                                                    <!--                                          :disabled="$cannot('product.create')"-->
                                                    <!--                                        />-->

                                                    <!--                                        <b-form-invalid-feedback v-bind="context.ariaMsg">-->
                                                    <!--                                          {{ context.errors[0] }}-->
                                                    <!--                                        </b-form-invalid-feedback>-->
                                                    <!--                                      </validation-provider>-->
                                                    <!--                                    </b-col>-->

                                                    <!--                                    <b-col cols="auto" v-if="whats_included.length > 1">-->
                                                    <!--                                      <b-button-->
                                                    <!--                                        variant="light"-->
                                                    <!--                                        @click="removeListItem(whats_included, index, defaultWhatsIncluded)"-->
                                                    <!--                                      >-->
                                                    <!--                                        &times;-->
                                                    <!--                                      </b-button>-->
                                                    <!--                                    </b-col>-->
                                                    <!--                                  </b-row>-->
                                                    <!--                                </draggable>-->
                                                    <!--                                <b-row align-h="end">-->
                                                    <!--                                  <b-col cols="auto">-->
                                                    <!--                                    <b-button variant="link" @click="addListItem(whats_included, defaultWhatsIncluded)">-->
                                                    <!--                                      Add Row-->
                                                    <!--                                    </b-button>-->
                                                    <!--                                  </b-col>-->
                                                    <!--                                </b-row>-->
                                                    <!--                              </b-form-group>-->
                                                      </b-card>
                                                    </div>
                                                    <hr />
                                                    <h4><i class="far fa-heading fa-fw" /> SEO</h4>
                                                    <b-card>
                                                        <b-form-group label="Meta Title">
                                                            <b-form-input
                                                                placeholder="e.g you need a big bowl"
                                                                v-model="seo.meta_title"
                                                                :disabled="$cannot('product.create')"
                                                            />
                                                        </b-form-group>

                                                        <b-form-group label="Meta Description">
                                                            <b-form-textarea
                                                                placeholder="e.g ..."
                                                                v-model="seo.meta_description"
                                                                :disabled="$cannot('product.create')"
                                                            />
                                                            <b-form-text>
                                                              {{ 225 - (seo.meta_description?.length ?? 0) }} / 225 characaters remaing
                                                            </b-form-text>
                                                        </b-form-group>

                                                        <b-form-group label="No Index">
                                                            <b-form-checkbox v-model="seo.no_index" :disabled="$cannot('product.create')">
                                                                Enable No-Index
                                                            </b-form-checkbox>
                                                        </b-form-group>
                                                    </b-card>
                                                </b-col>

                                                <b-col cols="4">
                                                    <b-button variant="primary" block :disabled="$cannot('product.create')" type="submit">
                                                        {{ buttonText }}
                                                        <b-spinner v-show="isLoading" class="ml-2" small label="Loading" />
                                                    </b-button>
                                                    <hr />
                                                    <h4><i class="far fa-heading fa-fw" /> Type</h4>
                                                    <b-card>
                                                        <validation-provider vid="type" name="Type" v-slot="context" rules="required">
                                                            <b-form-group label="Type">
                                                                <b-form-select
                                                                    id="formProductTypeField"
                                                                    v-model="type"
                                                                    :options="[
                                                                        { value: 'product', text: 'Product' },
                                                                        { value: 'digital', text: 'Digital' },
                                                                        { value: 'hotel', text: 'Hotel' },
                                                                        { value: 'themed', text: 'Themed' },
                                                                        { value: 'live', text: 'Live' },
                                                                        { value: 'experience_cash_voucher', text: 'Experience Cash Voucher' },
                                                                        { value: 'bundle_product', text: 'Bundle Product' },
                                                                        { value: 'bundle_cash_voucher', text: 'Bundle Cash Voucher Product' },
                                                                        { value: 'tier_product', text: 'Tier Product' },
                                                                      ]"
                                                                    :state="getValidationState(context)"
                                                                    aria-describedby="formProductTypeFieldError"
                                                                    v-bind="context.ariaInput"
                                                                    :disabled="$cannot('product.create')"
                                                                />

                                                                <b-form-invalid-feedback id="formProductTypeFieldError" v-bind="context.ariaMsg">
                                                                    {{ context.errors[0] }}
                                                                </b-form-invalid-feedback>
                                                            </b-form-group>
                                                        </validation-provider>
                                                    </b-card>

                                                    <div class="mt-3">
                                                        <h4><i class="far fa-heading fa-fw" /> Details</h4>
                                                        <b-card>
                                                            <validation-provider vid="details" name="detail" v-slot="context" >
                                                                <b-form-group>
                                                                    <multi-select
                                                                        v-model="details"
                                                                        :options="detailOptions"
                                                                        :multiple="true"
                                                                        :close-on-select="false"
                                                                        :clear-on-select="false"
                                                                        :preserve-search="true"
                                                                        placeholder="Choose details"
                                                                    />
                                                                    <b-form-invalid-feedback id="formProductDetailsFieldError" v-bind="context.ariaMsg">
                                                                        {{ context.errors[0] }}
                                                                    </b-form-invalid-feedback>
                                                                </b-form-group>
                                                            </validation-provider>
                                                        </b-card>
                                                    </div>

                                                    <div class="mt-3">
                                                        <h4><i class="far fa-heading fa-fw" /> Discount Price</h4>
                                                        <b-card>
                                                            <validation-provider vid="discount" name="Discount" v-slot="context" rules="required">
                                                                <b-form-group label="Discount">
                                                                    <b-form-input
                                                                        id="discount"
                                                                        v-model="discount"
                                                                        :state="getValidationState(context)"
                                                                        aria-describedby="discount"
                                                                        v-bind="context.ariaInput"
                                                                    />

                                                                    <b-form-invalid-feedback id="discount" v-bind="context.ariaMsg">
                                                                        {{ context.errors[0] }}
                                                                    </b-form-invalid-feedback>
                                                                </b-form-group>
                                                            </validation-provider>
                                                        </b-card>
                                                    </div>

                                                    <div class="mt-3">
                                                        <settings-group title="Gallery format" icon="fas fa-image">
                                                            <setting-toggle title="New for desktop" :status="new_gallery_desktop" @change="toggleGalleryDesktop" />
                                                            <setting-toggle title="New for mobile" :status="new_gallery_mobile" @change="toggleGalleryMobile" />
                                                        </settings-group>
                                                    </div>

                                                    <div class="mt-3">
                                                        <settings-group title="Climate" icon="fas fa-leaf">
                                                            <setting-toggle title="Climate friendly" :status="climate_friendly" @change="toggleClimateFriendly" />
                                                            <setting-toggle title="Climate compensated" :status="climate_compensated" @change="toggleClimateCompensated" />
                                                        </settings-group>
                                                    </div>

                                                    <div class="mt-3">
                                                        <settings-group title="Discoverability" icon="far fa-eye fa-fw">
                                                            <setting-toggle
                                                                title="Status"
                                                                description="Disable to set all statuses and variations to inactive"
                                                                :status="status"
                                                                @change="toggleStatus"
                                                            />
                                                            <setting-toggle
                                                                title="Listable"
                                                                description="Enable to show product on shop and in categories"
                                                                :status="is_listable"
                                                                @change="toggleListable"
                                                            />
                                                            <setting-toggle title="Top 25 / Popular" :status="is_top_20" @change="togglePopular" />
                                                            <setting-toggle title="Promoted" :status="is_promoted" @change="togglePromoted" />
                                                            <setting-toggle
                                                                title="Excluded from the feed"
                                                                description="Enable to exclude product from the Meta feed file"
                                                                :status="is_excluded_from_feed"
                                                                @change="toggleExcludedFromFeed"
                                                            />
                                                        </settings-group>
                                                    </div>

                                                    <h4 class="mt-3"><i class="far fa-heat fa-fw" /> Trends</h4>
                                                    <b-card>
                                                        <b-form-group label="Pulse" label-for="formPulseField">
                                                            <b-form-spinbutton
                                                                id="formPulseField"
                                                                v-model="pulse"
                                                                min="1"
                                                                max="3"
                                                                :disabled="$cannot('product.create')"
                                                            />
                                                        </b-form-group>

                                                        <validation-provider vid="heat" name="Heat" v-slot="context" rules="required">
                                                            <b-form-group label="Heat" label-for="formHeatField">
                                                                <b-form-input
                                                                    type="text"
                                                                    id="formHeatField"
                                                                    v-model="heat"
                                                                    :state="getValidationState(context)"
                                                                    :disabled="$cannot('product.create')"
                                                                    aria-describedby="formHeatFieldError"
                                                                    v-bind="context.ariaInput"
                                                                />

                                                                <b-form-invalid-feedback id="formHeatFieldError" v-bind="context.ariaMsg">{{
                                                                        context.errors[0]
                                                                    }}</b-form-invalid-feedback>
                                                            </b-form-group>
                                                        </validation-provider>
                                                    </b-card>

                                                    <h4 class="mt-3"><i class="far fa-eye fa-fw" /> Main Category</h4>
                                                    <b-card>
                                                        <validation-provider vid="main_category_id" name="Main Category" v-slot="context" rules="required">
                                                            <multi-select
                                                                v-model="main_category"
                                                                :options="mainCategories"
                                                                :preserve-search="true"
                                                                :disabled="$cannot('product.create')"
                                                                placeholder="Choose main category"
                                                                select-label=""
                                                                deselect-label=""
                                                                selected-label=""
                                                                label="name"
                                                                track-by="id"
                                                                @input="() => selectedSubCategories = []"
                                                            />

                                                            <div class="text-red" v-if="context.errors.length">
                                                                {{ context.errors[0] }}
                                                            </div>

                                                            <b-form-invalid-feedback id="formMainCategoryFieldError" v-bind="context.ariaMsg">
                                                                {{context.errors[0]}}
                                                            </b-form-invalid-feedback>
                                                        </validation-provider>
                                                    </b-card>

                                                    <h4 class="mt-3"><i class="far fa-eye fa-fw" /> Sub categories</h4>
                                                    <b-card>
                                                        <validation-provider vid="sub_categories" name="Sub categories" v-slot="context" rules="required">
                                                            <multi-select
                                                                v-model="selectedSubCategories"
                                                                :options="subCategories"
                                                                :multiple="true"
                                                                :close-on-select="false"
                                                                :clear-on-select="false"
                                                                :preserve-search="true"
                                                                select-label=""
                                                                deselect-label=""
                                                                selected-label=""
                                                                :disabled="$cannot('product.create') || !main_category"
                                                                placeholder="Choose sub-categories"
                                                                label="name"
                                                                track-by="id"
                                                            />

                                                            <div class="text-red" v-if="context.errors.length">
                                                                {{ context.errors[0] }}
                                                            </div>

                                                            <b-form-invalid-feedback v-bind="context.ariaMsg">
                                                                {{context.errors[0] }}
                                                            </b-form-invalid-feedback>
                                                        </validation-provider>
                                                    </b-card>

                                                    <h4 class="mt-3"><i class="far fa-eye fa-fw" /> Additional categories</h4>
                                                    <b-card>
                                                        <validation-provider vid="additional_categories" name="Additional categories" v-slot="context" rules="required">
                                                            <multi-select
                                                                v-model="selectedAdditionalCategories"
                                                                :options="additionalCategories"
                                                                :multiple="true"
                                                                :close-on-select="false"
                                                                :clear-on-select="false"
                                                                :preserve-search="true"
                                                                select-label=""
                                                                deselect-label=""
                                                                selected-label=""
                                                                :disabled="$cannot('product.create')"
                                                                placeholder="Choose additional categories"
                                                                label="name"
                                                                track-by="id"
                                                            />

                                                            <div class="text-red" v-if="context.errors.length">
                                                                {{ context.errors[0] }}
                                                            </div>

                                                            <b-form-invalid-feedback v-bind="context.ariaMsg">
                                                                {{context.errors[0] }}
                                                            </b-form-invalid-feedback>
                                                        </validation-provider>
                                                    </b-card>

                                                    <h4 class="mt-3"><i class="far fa-eye fa-fw" /> Tags</h4>
                                                    <b-card>
                                                        <b-form-group>
                                                            <multi-select
                                                                v-model="tags"
                                                                :options="availableTags"
                                                                :multiple="true"
                                                                :taggable="true"
                                                                :close-on-select="false"
                                                                :clear-on-select="false"
                                                                :preserve-search="true"
                                                                placeholder="Choose tags"
                                                                @tag="addTag"
                                                                @search-change="searchTags"
                                                            />
                                                        </b-form-group>
                                                    </b-card>
                                                </b-col>
                                            </b-row>
                                        </form>
                                    </validation-observer>
                                </b-tab>
                                <b-tab
                                    title="Connections"
                                    :active="
                    $route.name === 'products.edit.variations' ||
                      $route.name === 'product.variations.edit' ||
                      $route.name === 'product.variations.new'
                  "
                                    :disabled="!isEditPage"
                                    lazy
                                    @click="tabGoTo(links.variations)"
                                >
                                    <product-variation v-if="$route.name === 'products.edit.variations'" />
                                    <products-variation-form v-else />
                                </b-tab>
                                <b-tab
                                    title="Images"
                                    :active="$route.name === 'products.edit.images'"
                                    :disabled="!isEditPage"
                                    lazy
                                    @click="tabGoTo(links.images)"
                                >
                                    <product-images :product="product" @updated="loadContent" />
                                </b-tab>
                                <b-tab
                                    title="Reviews"
                                    :active="$route.name === 'products.edit.reviews'"
                                    :disabled="!isEditPage"
                                    lazy
                                    @click="tabGoTo(links.reviews)"
                                >
                                    <product-reviews :product="product" />
                                </b-tab>
                            </b-tabs>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css'
import EmojiPicker from 'vue-emoji-picker'

// import Draggable from 'vuedraggable'
import MultiSelect from 'vue-multiselect'
import {mapGetters} from 'vuex'
import SettingsGroup from '../../components/SettingsGroup'
import SettingToggle from '../../components/SettingToggle'
import ProductVariation from './ProductsVariation'
import ProductImages from './ProductImages'
import ProductReviews from './ProductReviews'
import {getValidationState} from '../page-helpers'
import {media, products, tags as productTag} from '@/api'
import WysiwygEditor from '@/components/WysiwygEditor.vue'
import ProductsVariationForm from '@/pages/Products/ProductsVariationForm'

export default {
    name: 'ProductsFormPage',
    inject: ['$cannot'],
    components: {
        // Draggable,
        SettingsGroup,
        SettingToggle,
        MultiSelect,
        ProductVariation,
        ProductImages,
        ProductReviews,
        WysiwygEditor,
        ProductsVariationForm,
        EmojiPicker,
    },
    data() {
        return {
            availableTags: [],
            type: 'product',
            product: null,
            thumbnail: null,
            main_category: null,
            selectedSubCategories: [],
            selectedAdditionalCategories: [],
            bullet_points: null,
            custom_useful_info: null,
            hotel_map_only: false,
            is_non_hotel: false,
            hotel_count: null,
            hotel_content: [],
            experience_cash_voucher_data: [],
            experience_cash_voucher_description: null,
            experience_cash_voucher_amount_description: null,
            experience_cash_voucher_product_variation_data: [],
            themed_content_description: null,
            content: '',
            heat: 10000,
            id: '',
            new_gallery_desktop: false,
            new_gallery_mobile: false,
            status: false,
            is_listable: false,
            is_popular: false,
            is_top_20: false,
            is_promoted: false,
            climate_friendly: false,
            climate_compensated: false,
            is_excluded_from_feed: false,
            punchline: '',
            lead: '',
            experience_description: '',
            tier_name: '',
            tier_name_buybox: '',
            hide_tier_names: false,
            name: '',
            pulse: 1,
            slug: '',
            uploading: {
              desktop: false,
              mobile: false,
            },
            video: {
              desktop: '',
              mobile: '',
              mobile_same_as_desktop: true,
            },
            video_content: '',
            how_it_works: '',
            tags: [],
            shopping_list: [{ name: '', price: null }],
            downloadable_shopping_list: [{ name: '', price: null, quantity: 1, custom_content: '' }],
            what_is_learned_title: '',
            what_is_learned: [{ title: '' }],
            whats_included: [{ title: '', icon: 'access' }],
            participants_text: '',
            when: '',
            duration: '',
            requirements: '',
            min_age: 3,
            voucher_description_text: '',
            seo: {
                meta_title: '',
                meta_description: '',
                no_index: false,
            },
            isLoading: false,
            discount: '',
            details: [],
            detailOptions: [],
            activeProductVariations: [],
            allActiveProductVariations: [],
            allActiveProductVariationsWithDetails: [],
            bundleCombinations: [],
            cashVoucherProducts: [],
            experience_cash_voucher_min: null,
            experience_cash_voucher_max: null,
            tier_product_data: [],
            emojiSearch: '',
        }
    },
    beforeRouteEnter(from, to, next) {
        next(vm => vm.loadContent())
    },
    computed: {
        ...mapGetters({
            allCategories: 'base/categories',
        }),
        places() {
            const seen = new Set()
            return this.allActiveProductVariationsWithDetails
                .map(v => ({ id: v.place_id, name: v.city_name }))
                .filter(p => {
                    if (seen.has(p.name)) return false
                    seen.add(p.name)
                    return true
                })
        },
        isEditPage() {
            return this.$route.name !== 'products.new'
        },
        pageTitle() {
            return this.isEditPage ? 'Edit Product' : 'New Product'
        },
        breadcrumbTitle() {
            return this.isEditPage ? 'EDIT' : 'NEW'
        },
        buttonText() {
            return `${this.isEditPage ? 'Save' : 'Create'} Product`
        },

        mainCategories() {
            const categories = this.allCategories.filter(c => c.type === 'category' && c.parent_id === null);

            categories.unshift({name: 'Choose a main category', $isDisabled: true})

            return categories;
        },

        subCategories() {
            const categories = this.allCategories.filter(c => c.type === 'category' && c.parent_id);

            categories.unshift({name: 'Choose multiple sub-categories', $isDisabled: true})

            return categories;
        },

        additionalCategories() {
            const categories = this.allCategories.filter(c => c.type === 'gifttips');

            categories.unshift({name: 'Choose multiple additional categories', $isDisabled: true})

            return categories;
        },

        links() {
            const id = this.$route.params.id

            return {
                edit: { name: 'products.edit', params: { id } },
                variations: { name: 'products.edit.variations', params: { id } },
                images: { name: 'products.edit.images', params: { id } },
                reviews: {
                    name: 'products.edit.reviews',
                    params: { id },
                    query: { tab: 'pending' },
                },
            }
        },
        defaultShoppingList() {
            return { name: '', price: null }
        },
        defaultWhatIsLearned() {
            return { title: '' }
        },
        defaultWhatsIncluded() {
            return { title: '', icon: 'access' }
        },
        whatsIncludedIcons() {
            return [
                'access',
                'alarm',
                'certificate',
                'chat',
                'devices',
                'download',
                'faq',
                'id',
                'list',
                'live',
                'material',
                'moderator',
                'repeat',
                'shoppinglist',
                'star',
                'trophy',
                'video',
            ]
        },
    },
    methods: {
        insertEmoji(emoji, index) {
          this.bullet_points[index].emoji = emoji
        },
        getValidationState,
        async getDetails() {
            const response = await products.details();

            return response.data.map(item => item.name);
        },

        variationsByPlace() {
            return this.allActiveProductVariationsWithDetails
                .map(v => ({ id: v.id, name: v.full_name }))
        },
        addTierProduct() {
            this.tier_product_data.push({
                place_id: null,
                variation_id: null,
                description: '',
                status: '',
                content: '',
            })
        },
        removeTierProduct(index) {
            this.tier_product_data.splice(index, 1)
        },

        async getActiveProductVariations() {
            const response = await products.activeProductVariations();
            return response.data.map(item => ({
                id: item.id,
                name: item.name
            }));
        },

      async getActiveProductVariationsWithDetails() {
        const response = await products.activeProductVariationsWithDetails()
        return response.data
      },

        async getActiveCashVoucherProducts() {
            const response = await products.activeCashVoucherProducts();
            return response.data.map(item => ({
                id: item.id,
                name: item.name
            }));
        },

        filteredCashVoucherProducts(selectedMainVariationId) {
            if (!this.cashVoucherProducts) return []
            if (!selectedMainVariationId) return this.cashVoucherProducts
            return this.cashVoucherProducts.filter(
                cv => cv.id !== selectedMainVariationId
            )
        },

        addCombination() {
            this.bundleCombinations.push({ variation1: null, variation2: null, amount: 0 });
        },

        removeCombination(index) {
            this.bundleCombinations.splice(index, 1);
        },

        filteredVariations(selectedVariationId) {
            if (!selectedVariationId) return this.allActiveProductVariations;
            return this.allActiveProductVariations.filter(variation => variation.id !== selectedVariationId);
        },

        async loadContent() {
            this.detailOptions = await this.getDetails();
            this.allActiveProductVariations = await this.getActiveProductVariations();
            this.allActiveProductVariationsWithDetails = await this.getActiveProductVariationsWithDetails();
            this.cashVoucherProducts = await this.getActiveCashVoucherProducts();
            if (!this.isEditPage) return

            const product = await this.$store.dispatch('products/find', this.$route.params.id)

            this.product = product

            this.type = product.product_type ?? 'product'
            this.bullet_points = product.bullet_points ?? null
            this.custom_useful_info = product.custom_useful_info ?? null
            this.activeProductVariations = product.active_product_variations ?? null
            this.cashVoucherProducts = product.cash_voucher_products ?? null
            this.hotel_content = product.hotel_content ?? null
            this.experience_cash_voucher_data = product.experience_cash_voucher_data ?? null
            this.experience_cash_voucher_description = product.experience_cash_voucher_description ?? null
            this.experience_cash_voucher_amount_description = product.experience_cash_voucher_amount_description ?? null
            this.experience_cash_voucher_product_variation_data = product.experience_cash_voucher_product_variation_data ?? null
            this.themed_content_description = product.themed_content_description ?? null
            this.experience_cash_voucher_error_message = product.experience_cash_voucher_error_message ?? null
            this.hotel_count = product.hotel_count ?? null
            this.hotel_map_only = product.hotel_map_only ?? false
            this.is_non_hotel = product.is_non_hotel ?? false
            this.content = product.content
            this.heat = product.heat
            this.id = product.id
            this.new_gallery_desktop = product.new_gallery_desktop
            this.new_gallery_mobile = product.new_gallery_mobile
            this.is_listable = product.is_listable
            this.is_top_20 = product.is_top_20
            this.is_promoted = product.is_promoted
            this.climate_friendly = product.climate_friendly
            this.climate_compensated = product.climate_compensated
            this.is_excluded_from_feed = product.is_excluded_from_feed
            this.punchline = product.punchline
            this.lead = product.lead
            this.video.desktop = product.video_desktop
            this.video.mobile = product.video_mobile
            this.video.mobile_same_as_desktop = product.video_mobile === '' || product.video_mobile === null
            this.experience_description = product.experience_description
            this.tier_name = product.tier_name
            this.tier_name_buybox = product.tier_name_buybox
            this.hide_tier_names = product.hide_tier_names
            this.main_category = product.main_category
            this.selectedSubCategories = product.categories.filter(c => c.type === 'category')
            this.selectedAdditionalCategories = product.categories.filter(c => c.type === 'gifttips')
            this.name = product.name
            this.slug = product.slug
            this.pulse = product.pulse
            this.status = product.status
            this.thumbnail = product.thumbnail
            this.tags = product.tags
            this.video_content = product.video_content
            this.how_it_works = product.how_it_works
            this.shopping_list = product.shopping_list ?? []
            this.downloadable_shopping_list = product.downloadable_shopping_list ?? []
            this.what_is_learned_title = product.what_is_learned_title
            this.what_is_learned = product.what_is_learned ?? []
            this.whats_included = product.whats_included ?? []
            this.participants_text = product.participants_text
            this.when = product.when
            this.duration = product.duration
            this.requirements = product.requirements
            this.min_age = product.min_age
            this.voucher_description_text = product.voucher_description_text
            this.seo = product.seo
            this.details = product.details
            this.activeProductVariations = product.active_product_variations
            this.discount = product.discount
            this.experience_cash_voucher_min = product.experience_cash_voucher_min
            this.experience_cash_voucher_max = product.experience_cash_voucher_max

            if (product.bundle_products && product.bundle_products.length) {
                this.bundleCombinations = product.bundle_products.map(bundle => {
                    const variation1 = this.allActiveProductVariations
                        .find(v => v.id === bundle.main_product_variation_id);

                    const variation2 = bundle.type === 'bundle_product'
                        ? this.allActiveProductVariations
                            .find(v => v.id === bundle.product_variation_id)
                        : bundle.cash_voucher_product_id === 0
                            ? { id: 0, name: 'Open Giftcard' }
                            : this.cashVoucherProducts
                                .find(cv => cv.id === bundle.cash_voucher_product_id);

                    const amount = bundle.amount ?? 0;

                    return { variation1, variation2, amount };
                });
            }

            const raw = product.tier_product_data || []
            const grouped = raw.reduce((acc, item) => {
                const key = item.variation_id.id

                const placeObj = this.places.find(p => p.id === item.place_id)
                const variationObj = this.variationsByPlace().find(v => v.id === item.variation_id.id)

                if (!acc[key]) {
                    acc[key] = {
                        order: item.id,
                        place_id: [],
                        variation_id: variationObj,
                        description: item.description || '',
                        status: item.status || '',
                        content: item.content || '',
                    }
                }
                acc[key].place_id.push(placeObj)
                return acc
            }, {})

            this.tier_product_data = Object.values(grouped).sort((curr, prev) => curr.order - prev.order)
        },
        tabGoTo(route) {
            if (route.name !== this.$route.name) {
                this.$router.push(route)
            }
        },
        pick(event, index) {
            const file = event.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (e) => {
                    this.hotel_content[index].image = e.target.result;
                };
            }
        },
        addTag(newTag) {
            this.availableTags.push(newTag)
            this.tags.push(newTag)
        },
        async searchTags(query) {
            const tags = await productTag.all({
                filter: {
                    name: query,
                },
            })

            this.availableTags = tags.data().map(t => t.name)
        },
        toggleStatus() {
            this.status = !this.status
        },
        toggleListable(status) {
            this.is_listable = status
        },
        togglePopular(status) {
            this.is_top_20 = status
        },
        togglePromoted(status) {
            this.is_promoted = status
        },
        toggleClimateFriendly(status) {
            this.climate_friendly = status
        },
        toggleClimateCompensated(status) {
            this.climate_compensated = status
        },
        toggleGalleryDesktop(status) {
            this.new_gallery_desktop = status
        },
        toggleGalleryMobile(status) {
            this.new_gallery_mobile = status
        },
        toggleExcludedFromFeed(status) {
            this.is_excluded_from_feed = status
        },
        async submitFormData() {
            const data = {
                main_category_id: this.main_category ? this.main_category.id : null,
                bullet_points: this.bullet_points,
                custom_useful_info: this.custom_useful_info,
                hotel_content: this.hotel_content,
                experience_cash_voucher_data: this.experience_cash_voucher_data,
                experience_cash_voucher_description: this.experience_cash_voucher_description,
                experience_cash_voucher_amount_description: this.experience_cash_voucher_amount_description,
                experience_cash_voucher_product_variation_data: this.experience_cash_voucher_product_variation_data,
                themed_content_description: this.themed_content_description,
                experience_cash_voucher_error_message: this.experience_cash_voucher_error_message,
                active_product_variations: this.activeProductVariations?.length
                    ? this.activeProductVariations?.map(variation => variation.id)
                    : null,
                hotel_count: this.hotel_count,
                hotel_map_only: this.hotel_map_only,
                is_non_hotel: this.is_non_hotel,
                content: this.content,
                heat: +this.heat,
                new_gallery_desktop: this.new_gallery_desktop,
                new_gallery_mobile: this.new_gallery_mobile,
                status: this.status,
                is_listable: this.is_listable,
                is_top_20: this.is_top_20,
                is_promoted: this.is_promoted,
                climate_friendly: this.climate_friendly,
                climate_compensated: this.climate_compensated,
                is_excluded_from_feed: this.is_excluded_from_feed,
                punchline: this.punchline,
                lead: this.lead,
                video_desktop: this.video.desktop,
                video_mobile: this.video.mobile,
                experience_description: this.experience_description,
                tier_name: this.tier_name,
                tier_name_buybox: this.tier_name_buybox,
                hide_tier_names: this.hide_tier_names,
                name: this.name,
                pulse: this.pulse,
                slug: this.slug,
                video_content: this.video_content,
                sub_categories: this.selectedSubCategories.length ? this.selectedSubCategories.map(c => c.id) : null,
                additional_categories: this.selectedAdditionalCategories.length ? this.selectedAdditionalCategories.map(c => c.id) : null,
                how_it_works: this.how_it_works,
                shopping_list: this.shopping_list.length ? this.shopping_list : null,
                downloadable_shopping_list: this.downloadable_shopping_list.length ? this.downloadable_shopping_list : null,
                what_is_learned_title: this.what_is_learned_title,
                what_is_learned: this.what_is_learned.length ? this.what_is_learned : null,
                whats_included: this.whats_included.length ? this.whats_included : null,
                participants_text: this.participants_text,
                when: this.when,
                tags: this.tags.length ? this.tags : null,
                duration: this.duration,
                requirements: this.requirements,
                min_age: this.min_age,
                voucher_description_text: this.voucher_description_text,
                seo: this.seo,
                product_type: this.type,
                details: this.details,
                discount: this.discount,
                experience_cash_voucher_min: this.experience_cash_voucher_min,
                experience_cash_voucher_max: this.experience_cash_voucher_max,
                bundle_variation_combinations: this.bundleCombinations.length
                    ? this.bundleCombinations.map(c => ({
                        main_product_variation_id: c.variation1?.id ?? null,
                        product_variation_id: c.variation2?.id ?? null,
                        bundle_cash_voucher_amount: c.amount,
                    }))
                    : null,
                tier_product_data: this.tier_product_data.length
                    ? this.tier_product_data.map(c => ({
                        place_id: c.place_id,
                        variation_id: c.variation_id,
                        description: c.description,
                        status: c.status,
                        content: c.content,
                    }))
                    : null,
            }

            this.isLoading = true
            await this.$store.dispatch('auth/refreshToken');

            if (this.isEditPage) {
                await this.handleProductUpdate({ ...data, id: this.id })
            } else {
                await this.handleProductCreation(data)
            }
        },
        async handleProductCreation(payload) {
            try {
                await this.$store.dispatch('auth/refreshToken');
                const product = await this.$store.dispatch('products/create', payload)
                // this.$router.push({ name: 'products.edit', params: { id: product.data.id } }) // @todo to be investigated
                window.location.href = `/dashboard/products/${product.data.id}/edit`

                this.$bvToast.toast(`Product "${this.name}" created.`, {
                    title: 'Product Created',
                    variant: 'primary',
                })
            } catch (error) {
                this.$refs.productsForm.setErrors(error.errors)
            } finally {
                this.isLoading = false
            }
        },
        async handleProductUpdate(payload) {
            try {
                await this.$store.dispatch('auth/refreshToken');
                await this.$store.dispatch('products/update', payload)
                this.$bvToast.toast(`Product "${this.name}" updated.`, {
                    title: 'Product Updated',
                    variant: 'primary',
                })
            } catch (error) {
                this.$refs.productsForm.setErrors(error.errors)
                this.$bvToast.toast(`There where validation errors.`, {
                    title: 'Product could not be updated',
                    variant: 'danger',
                })
            } finally {
                this.isLoading = false
            }
        },
        removeBulletPoint(index) {
            if (this.bullet_points.length === 1) {
                return (this.bullet_points = null)
            }

            this.bullet_points.splice(index, 1)
        },
      removeCustomUsefulInfo(index) {
            if (this.custom_useful_info.length === 1) {
                return (this.custom_useful_info = null)
            }

            this.custom_useful_info.splice(index, 1)
        },
        addEmptyBulletPoint() {
            this.bullet_points ? this.bullet_points.push({ emoji: '', content: '' }) : this.bullet_points = [{ emoji: '', content: '' }]
        },
        addEmptyCustomUsefulInfo() {
            this.custom_useful_info ? this.custom_useful_info.push({ info: '', details: '' }) : this.custom_useful_info = [{ info: '', details: '' }]
        },
        addEmptyHotelContent() {
            this.hotel_content ? this.hotel_content.push({ content: '' }) : this.hotel_content = [{ content: '' }]
        },
        addExperienceCashVoucherData() {
            this.experience_cash_voucher_data ? this.experience_cash_voucher_data.push({ name: '' }) : this.experience_cash_voucher_data = [{ name: '' }]
        },
        removeHotelContent(index) {
            if (this.hotel_content.length === 1) {
                return (this.hotel_content = null)
            }

            this.hotel_content.splice(index, 1)
        },
        removeExperienceCashVoucherData(index) {
            if (this.experience_cash_voucher_data.length === 1) {
                return (this.experience_cash_voucher_data = null)
            }

            this.experience_cash_voucher_data.splice(index, 1)
        },
        addListItem(target, content) {
            target.push(content)
        },
        removeListItem(target, index, content) {
            if (target.length === 1) {
                return this.addListItem(target, content)
            }

            target.splice(index, 1)
        },
        getIcon(id) {
            return `/img/icon/product_included/${id}.svg`
        },
        updateWhatLearnedIcon(index, icon) {
            const data = [...this.whats_included]
            data[index].icon = icon

            this.$set(this.whats_included, data)
        },

      toggleSameVideoForMobile(value) {
        this.video.mobile_same_as_desktop = value

        if (!value) {
          this.video.mobile = null
        }
      },

      pickDesktopVideo() {
        this.$refs.desktopVideo.click()
      },

      pickMobileVideo() {
        this.$refs.mobileVideo.click()
      },

      async uploadDesktopVideo(event) {
        try {
          this.uploading.desktop = true
          const video = await media.video(event.target.files[0], 'video-desktop')
          this.video.desktop = video.data.url
          this.uploading.desktop = false
        } catch (error) {
          this.$bvToast.toast(error.message, {
            title: 'Something went wrong.',
            variant: 'danger',
          })
        }
      },

      async uploadMobileVideo(event) {
        try {
          this.uploading.mobile = true
          const video = await media.video(event.target.files[0], 'video-mobile')
          this.video.mobile = video.data.url
          this.uploading.mobile = false
        } catch (error) {
          this.$bvToast.toast(error.message, {
            title: 'Something went wrong.',
            variant: 'danger',
          })
        }
      },

      getProductUrl() {
        const clientUrl = this.$store.getters['market/enabled']
          ? this.$store.getters['market/clientUrl']
          : process.env.VUE_APP_APP_URL

        return `${clientUrl}/opplevelser/${this.product.slug}`
      },
    },
  directives: {
    focus: {
      inserted(el) {
        el.focus()
      },
    },
  },
}
</script>

<style scoped>
.no-dimensions {
  width: 0;
  height: 0;
}
</style>

<style>
.emoji-invoker-wrapper {
  position: absolute;
  top: 0.4rem;
  left: 4.3rem;
  z-index: 4;
}
.emoji-invoker {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  background: transparent;
  border: 0;
}
.emoji-invoker > svg {
  fill: #b1c6d0;
}

.emoji-picker {
  position: absolute;
  z-index: 1;
  font-family: Montserrat;
  border: 1px solid #ccc;
  width: 15rem;
  height: 20rem;
  overflow-y: scroll;
  padding: 1rem;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 1px 1px 8px #c7dbe6;
}
.emoji-picker__search {
  display: flex;
  margin-bottom: 1rem;
}
.emoji-picker__search > input {
  flex: 1;
  border-radius: 10rem;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  outline: none;
}
.emoji-picker h5 {
  margin-bottom: 0;
  color: #b1b1b1;
  text-transform: uppercase;
  font-size: 0.8rem;
  cursor: default;
}
.emoji-picker .emojis {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 0.25rem;
}
.emoji-picker .emojis:after {
  content: "";
  flex: auto;
}
.emoji-picker .emojis span {
  padding: 0.2rem;
  cursor: pointer;
  border-radius: 5px;
}
.emoji-picker .emojis span:hover {
  background: #ececec;
  cursor: pointer;
}
</style>
